import React, { useEffect, useState, useCallback } from 'react';
import { useDashboardStore, useAuthStore, useCompanyStore } from '@/store';
import { useI18n } from '@/components/I18nProvider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Clock,
  Calendar,
  AlertTriangle,
  CheckCircle,
  MessageCircle,
  TrendingUp,
  Banknote,
  CreditCard,
  Building2,
  FileBarChart,
  Filter,
  RotateCcw,
  Users,
  FileText,
  CircleDollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Package,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ' TL';
};

const METHOD_COLORS = {
  'Nakit': '#22c55e',
  'EFT/Havale': '#3b82f6',
  'Kredi Kartı': '#f97316',
  'Vadeli Çek': '#8b5cf6',
  'Belirtilmemiş': '#94a3b8',
};

const STATUS_COLORS = {
  'pending': '#f59e0b',
  'overdue': '#ef4444',
  'paid': '#22c55e',
  'cancelled': '#94a3b8',
};

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight="bold">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const ReportSection = ({ title, icon: Icon, description, dateFrom, dateTo, onDateFromChange, onDateToChange, onFilter, onReset, testIdPrefix, children }) => (
  <Card data-testid={`${testIdPrefix}-card`}>
    <CardHeader className="pb-2 sm:pb-4">
      <CardTitle className="flex items-center gap-2 font-heading text-base sm:text-lg">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
        {title}
      </CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-5">
      <div className="flex flex-wrap items-end gap-2 sm:gap-3" data-testid={`${testIdPrefix}-filters`}>
        <div className="space-y-1">
          <Label className="text-[11px] sm:text-xs text-muted-foreground">Başlangıç</Label>
          <Input type="date" value={dateFrom} onChange={(e) => onDateFromChange(e.target.value)} className="h-9 w-[140px] sm:w-[160px] text-xs sm:text-sm" data-testid={`${testIdPrefix}-date-from`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] sm:text-xs text-muted-foreground">Bitiş</Label>
          <Input type="date" value={dateTo} onChange={(e) => onDateToChange(e.target.value)} className="h-9 w-[140px] sm:w-[160px] text-xs sm:text-sm" data-testid={`${testIdPrefix}-date-to`} />
        </div>
        <div className="flex gap-2">
          <Button size="sm" className="h-9" onClick={onFilter} data-testid={`${testIdPrefix}-filter-btn`}>
            <Filter className="mr-1.5 h-3.5 w-3.5" />Filtrele
          </Button>
          {(dateFrom || dateTo) && (
            <Button size="sm" variant="ghost" className="h-9" onClick={onReset} data-testid={`${testIdPrefix}-reset-btn`}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Temizle
            </Button>
          )}
        </div>
      </div>
      <Separator />
      {children}
    </CardContent>
  </Card>
);

export default function DashboardPage() {
  const { user } = useAuthStore();
  const { company } = useCompanyStore();
  const { t } = useI18n();
  const { stats, monthlyCollections, personnelPerformance, whatsappStatus, collectionReport, paymentReport, isLoading, fetchStats, fetchCollectionReport, fetchPaymentReport } = useDashboardStore();

  const [collDateFrom, setCollDateFrom] = useState('');
  const [collDateTo, setCollDateTo] = useState('');
  const [payDateFrom, setPayDateFrom] = useState('');
  const [payDateTo, setPayDateTo] = useState('');

  useEffect(() => {
    fetchStats();
    fetchCollectionReport();
    fetchPaymentReport();
  }, [fetchStats, fetchCollectionReport, fetchPaymentReport]);

  const handleCollFilter = useCallback(() => { fetchCollectionReport(collDateFrom || undefined, collDateTo || undefined); }, [fetchCollectionReport, collDateFrom, collDateTo]);
  const handleCollReset = useCallback(() => { setCollDateFrom(''); setCollDateTo(''); fetchCollectionReport(); }, [fetchCollectionReport]);
  const handlePayFilter = useCallback(() => { fetchPaymentReport(payDateFrom || undefined, payDateTo || undefined); }, [fetchPaymentReport, payDateFrom, payDateTo]);
  const handlePayReset = useCallback(() => { setPayDateFrom(''); setPayDateTo(''); fetchPaymentReport(); }, [fetchPaymentReport]);

  const chartColors = ['hsl(var(--primary))', '#10b981', 'hsl(var(--primary) / 0.7)', '#34d399', '#059669'];
  const isWhatsAppConnected = whatsappStatus?.status === 'connected';

  const collPieData = collectionReport?.by_method?.map((m) => ({ name: m.method, value: m.total, fill: METHOD_COLORS[m.method] || '#94a3b8' })) || [];
  const payPieData = paymentReport?.by_status?.map((s) => ({ name: s.status_label, value: s.total, fill: STATUS_COLORS[s.status] || '#94a3b8' })) || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header with package info */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-xl sm:text-3xl font-bold">Hoş geldiniz, {user?.name}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">İşte bugünkü özet</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {company?.package_name && (
            <Badge variant="secondary" className="gap-1.5 text-xs" data-testid="header-package-badge">
              <Package className="h-3 w-3" />{company.package_name}
            </Badge>
          )}
          {isWhatsAppConnected ? (
            <Badge className="bg-green-100 text-green-700 gap-1 text-xs"><MessageCircle className="h-3 w-3" />WA Bağlı</Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground gap-1 text-xs"><MessageCircle className="h-3 w-3" />WA Bağlı Değil</Badge>
          )}
        </div>
      </div>

      {/* Enhanced KPI Grid: 6 cards */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 xl:grid-cols-6" data-testid="kpi-grid">
        {[
          { title: t('pending'), value: formatCurrency(stats?.total_pending || 0), sub: `${stats?.pending_count || 0} ${t('paymentsLabel')}`, icon: Clock, gradient: 'from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20', iconColor: 'text-primary', borderColor: 'border-primary/20' },
          { title: t('todayDue'), value: formatCurrency(stats?.today_due || 0), sub: t('todayDueDesc'), icon: Calendar, gradient: 'from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20', iconColor: 'text-emerald-600 dark:text-emerald-400', borderColor: 'border-emerald-200 dark:border-emerald-800' },
          { title: t('overdue'), value: formatCurrency(stats?.total_overdue || 0), sub: `${stats?.overdue_count || 0} ${t('paymentsLabel')}`, icon: AlertTriangle, gradient: 'from-red-50 to-red-100/50 dark:from-red-950/30 dark:to-red-900/20', iconColor: 'text-red-600 dark:text-red-400', borderColor: 'border-red-200 dark:border-red-800' },
          { title: t('todayCollected'), value: formatCurrency(stats?.today_collected || 0), sub: t('todayCollectedDesc'), icon: CheckCircle, gradient: 'from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20', iconColor: 'text-green-600 dark:text-green-400', borderColor: 'border-green-200 dark:border-green-800' },
          { title: t('dailyMessage'), value: `${stats?.daily_message_count || 0}/${stats?.daily_message_limit || 100}`, sub: t('sentLimit'), icon: MessageCircle, gradient: 'from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20', iconColor: 'text-primary', borderColor: 'border-primary/20' },
          { title: t('monthlyMessage'), value: `${stats?.monthly_message_count || 0}/${stats?.monthly_message_limit || 3000}`, sub: t('sentLimit'), icon: TrendingUp, gradient: 'from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20', iconColor: 'text-emerald-600 dark:text-emerald-400', borderColor: 'border-emerald-200 dark:border-emerald-800' },
        ].map((kpi) => (
          <div
            key={kpi.title}
            className={`rounded-xl border ${kpi.borderColor} bg-gradient-to-br ${kpi.gradient} p-3 sm:p-4 transition-all hover:shadow-sm`}
            data-testid={`kpi-${kpi.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {isLoading ? <Skeleton className="h-16 w-full" /> : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <kpi.icon className={`h-4 w-4 ${kpi.iconColor}`} />
                  <span className="text-[11px] sm:text-xs font-medium text-muted-foreground">{kpi.title}</span>
                </div>
                <p className="font-heading text-sm sm:text-lg font-bold leading-tight">{kpi.value}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{kpi.sub}</p>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Two Reports Side by Side */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Ödeme Raporu */}
        <ReportSection
          title="Ödeme Raporu"
          icon={FileText}
          description="Ödeme durumuna göre dağılım"
          dateFrom={payDateFrom} dateTo={payDateTo}
          onDateFromChange={setPayDateFrom} onDateToChange={setPayDateTo}
          onFilter={handlePayFilter} onReset={handlePayReset}
          testIdPrefix="payment-report"
        >
          {/* Status Cards */}
          <div className="grid gap-2 grid-cols-2">
            <div className="rounded-xl border bg-gradient-to-br from-primary/5 to-primary/10 p-3" data-testid="payment-report-total">
              <div className="flex items-center gap-1.5 mb-1">
                <CircleDollarSign className="h-3.5 w-3.5 text-primary" />
                <p className="text-[11px] font-medium text-muted-foreground">Toplam Ödeme</p>
              </div>
              <p className="font-heading text-base sm:text-xl font-bold">{formatCurrency(paymentReport?.total_amount || 0)}</p>
              <p className="text-[10px] text-muted-foreground">{paymentReport?.total_count || 0} adet</p>
            </div>
            {paymentReport?.by_status?.map((s) => (
              <div key={s.status} className="rounded-xl border p-3" style={{ borderLeftColor: STATUS_COLORS[s.status], borderLeftWidth: '3px' }} data-testid={`payment-status-${s.status}`}>
                <p className="text-[11px] font-medium text-muted-foreground mb-1">{s.status_label}</p>
                <p className="font-heading text-base sm:text-lg font-bold">{formatCurrency(s.total)}</p>
                <p className="text-[10px] text-muted-foreground">{s.count} adet</p>
              </div>
            ))}
          </div>
          {payPieData.length > 0 ? (
            <div className="flex justify-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={payPieData} cx="50%" cy="50%" labelLine={false} label={CustomPieLabel} outerRadius={85} dataKey="value">
                    {payPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [formatCurrency(v), n]} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '11px' }} />
                  <Legend verticalAlign="bottom" height={30} formatter={(v) => <span className="text-xs">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-24 items-center justify-center text-xs text-muted-foreground">Henüz ödeme verisi yok</div>
          )}
        </ReportSection>

        {/* Tahsilat Raporu */}
        <ReportSection
          title="Tahsilat Raporu"
          icon={FileBarChart}
          description="Ödeme yöntemine göre tahsilat dağılımı"
          dateFrom={collDateFrom} dateTo={collDateTo}
          onDateFromChange={setCollDateFrom} onDateToChange={setCollDateTo}
          onFilter={handleCollFilter} onReset={handleCollReset}
          testIdPrefix="collection-report"
        >
          {/* Method Cards */}
          <div className="grid gap-2 grid-cols-2">
            <div className="rounded-xl border bg-gradient-to-br from-primary/5 to-primary/10 p-3" data-testid="collection-report-total">
              <div className="flex items-center gap-1.5 mb-1">
                <Banknote className="h-3.5 w-3.5 text-primary" />
                <p className="text-[11px] font-medium text-muted-foreground">Toplam Tahsilat</p>
              </div>
              <p className="font-heading text-base sm:text-xl font-bold">{formatCurrency(collectionReport?.total_collected || 0)}</p>
              <p className="text-[10px] text-muted-foreground">{collectionReport?.total_count || 0} işlem</p>
            </div>
            {collectionReport?.by_method?.map((m) => (
              <div key={m.method} className="rounded-xl border p-3" style={{ borderLeftColor: METHOD_COLORS[m.method] || '#94a3b8', borderLeftWidth: '3px' }} data-testid={`collection-method-${m.method.replace(/\//g, '-').toLowerCase()}`}>
                <p className="text-[11px] font-medium text-muted-foreground mb-1">{m.method}</p>
                <p className="font-heading text-base sm:text-lg font-bold">{formatCurrency(m.total)}</p>
                <p className="text-[10px] text-muted-foreground">{m.count} işlem</p>
              </div>
            ))}
          </div>
          {collPieData.length > 0 ? (
            <div className="flex justify-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={collPieData} cx="50%" cy="50%" labelLine={false} label={CustomPieLabel} outerRadius={85} dataKey="value">
                    {collPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [formatCurrency(v), n]} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '11px' }} />
                  <Legend verticalAlign="bottom" height={30} formatter={(v) => <span className="text-xs">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-24 items-center justify-center text-xs text-muted-foreground">Henüz tahsilat verisi yok</div>
          )}
        </ReportSection>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Monthly Collection Trend */}
        <Card>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 font-heading text-base sm:text-lg">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              Aylık Tahsilat Trendi
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {isLoading ? <Skeleton className="h-[200px] sm:h-[260px] w-full" /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyCollections} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={35} />
                  <Tooltip formatter={(v) => [formatCurrency(v), 'Tahsilat']} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {monthlyCollections.map((_, i) => <Cell key={i} fill={chartColors[i % chartColors.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Personnel Performance */}
        <Card>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 font-heading text-base sm:text-lg">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              Personel Performansı
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {isLoading ? <Skeleton className="h-[200px] sm:h-[260px] w-full" /> : personnelPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={personnelPerformance} layout="vertical" margin={{ top: 10, right: 10, left: 60, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="user_name" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} width={60} />
                  <Tooltip formatter={(v) => [formatCurrency(v), 'Tahsilat']} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                  <Bar dataKey="total_collected" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">Henüz performans verisi yok</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
